/**
 * Moving light, shared by the phones and the stage so the hall and the screen beat together. Everything runs on
 * the server clock: two phones (or a phone and the stage) computing the same mood at the same moment get the
 * same value. Each group keeps its own tempo for `rhythm`; `converge` blends those tempos into one over 20 s;
 * `sync` is one shared pulse; `unite` moves each group's colour into one shared light, a little later by seat.
 */
import type { Mood } from '../src/platform/types.ts';

export const TEMPO = [1.25, 1.7, 0.95, 2.2, 1.45, 1.1, 1.9];
export const SHARED = '#ffe2a8';
export const MOVING: Mood[] = ['rhythm', 'converge', 'sync', 'unite'];

/** A beat: sharp on the downbeat, decaying to nothing before the next. */
export const beat = (t: number, period: number) => { const f = ((t / 1000) / period) % 1; return Math.pow(1 - f, 2.6); };

export interface Lamp { glow: number; fill: number; color: string }

/**
 * The light of one source (a phone, or one group's pool on the stage) at server time `t`.
 * `delay` staggers `unite` so the change travels through the hall instead of flipping at once.
 */
export function lampAt(mood: Mood, t: number, o: { group: number; color: string; since: number; delay?: number }): Lamp {
  const idx = o.group % TEMPO.length;
  if (mood === 'rhythm') { const b = beat(t, TEMPO[idx]); return { glow: 0.25 + 0.75 * b, fill: 0.08 + 0.32 * b, color: o.color }; }
  if (mood === 'converge') {
    const k = Math.min(1, Math.max(0, (t - o.since) / 20000));
    const b = (1 - k) * beat(t, TEMPO[idx]) + k * beat(t, 1.5);
    return { glow: 0.25 + 0.75 * b, fill: 0.08 + 0.3 * b, color: o.color };
  }
  if (mood === 'sync') { const b = beat(t, 1.5); return { glow: 0.3 + 0.7 * b, fill: 0.1 + 0.35 * b, color: SHARED }; }
  if (mood === 'unite') {
    const k = Math.min(1, Math.max(0, (t - o.since - (o.delay ?? 0)) / 3500));
    const breathe = 0.5 + 0.5 * Math.sin((t / 1000) * Math.PI / 2.5);
    return { glow: 0.55 + 0.45 * (k * breathe + (1 - k)), fill: 0.18 + 0.32 * k, color: `color-mix(in srgb, ${o.color} ${Math.round((1 - k) * 100)}%, ${SHARED})` };
  }
  return { glow: 0, fill: 0, color: o.color };
}
